import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import moment from "moment";
import { toast } from "react-toastify";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid2,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconArrowDown, IconCheck } from "@tabler/icons-react";

import { extract, extractPayments } from "../services/finance";
import { antecipationGetStatus, antecipationStatus } from "../services/store";
import MercadoPagoTab from "../components/MercadoPagoTab";

import ContainerLoading from "../components/ContainerLoading";
import DateRanger from "../components/DateRanger";
import PaymentItem from "../components/PaymentItem";
import PageHeader from "../components/PageHeader";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { formatDate, formatPercentage, formatPrice } from "../helpers/format";
import { useUserStore } from "../stores/user";

const Finance = () => {
  const [value, setValue] = useState("1");
  const { user } = useUserStore();

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    setValue(newValue);
  };

  const [filters, setFilters] = useState({
    startDate: moment().startOf("month").toDate(),
    endDate: moment().toDate(),
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["extract", JSON.stringify(filters)],
    queryFn: () => extract(filters),
    initialData: { extract: [], overdue: [] },
  });

  console.log("data extract", data);

  const {
    data: extractPaymentsData,
    isLoading: isLoadingExtractPayments,
    isFetching: isFetchingExtractPayments,
    refetch: refetchExtractPayments,
  } = useQuery({
    queryKey: ["extract-payments", JSON.stringify(filters)],
    queryFn: () => extractPayments(filters),
  });

  const {
    data: dataAntecipation,
    isLoading: isLoadingAntecipation,
    isFetching: isFetchingAntecipation,
    refetch: refetchAntecipation,
  } = useQuery({
    queryKey: ["refetchAntecipation"],
    queryFn: antecipationGetStatus,
    initialData: { extract: [] },
  });

  const taxaAntecipacao = dataAntecipation?.taxAnticipation * 100;

  // ── Mercado Pago ──────────────────────────────────────────

  const { mutate, isPending } = useMutation({
    mutationFn: antecipationStatus,
    onSuccess: () => {
      refetchAntecipation();
      toast.success("Antecipação atualizada com sucesso.");
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(
        String(e?.response?.data?.message) ||
          "Tivermos um problema ao atualizar sua antencipação",
      );
    },
  });

  const loading =
    isLoading ||
    isFetching ||
    isPending ||
    isLoadingAntecipation ||
    isFetchingAntecipation ||
    isLoadingExtractPayments ||
    isFetchingExtractPayments;

  return (
    <ContainerLoading loading={loading}>
      <Grid2 spacing={2} padding={2} container>
        <PageHeader
          title="Financeiro"
          subtitle="Todas as informações financeiras em um só lugar."
        />
        {/* <Grid2
          sx={{
            backgroundColor: "secondary.dark",
            padding: 2,
            borderRadius: 2,
          }}
          size={{ xs: 12, sm: 6, lg: 3 }}
        >
          {/* <Stack
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography fontSize={12} color="secondary.contrastText">
                Saldo para resgate
              </Typography>
              <Tooltip title="Este é o valor que já pode ser regastado, referente a pagamentos pagos ou debitados.">
                <IconButton size="small" sx={{ padding: 0 }}>
                  <InfoOutlinedIcon sx={{ color: "#7600ED", fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography
              fontSize={22}
              fontWeight={700}
              color={
                data?.balance < 0
                  ? "error"
                  : data?.balance > 0
                    ? "success"
                    : "text.primary"
              }
            >
              {formatPrice(data?.balance)}
            </Typography>
          </Stack> */}
        <Grid2
          sx={{
            backgroundColor: "secondary.dark",
            padding: 2,
            borderRadius: 2,
          }}
          size={{ xs: 12, sm: 6, lg: 4 }}
        >
          <Stack
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography fontSize={12} color="secondary.contrastText">
                Pagamentos Confirmados
              </Typography>
              <Tooltip title="Este valor já foi confirmado e está em processo de liquidação, especialmente no caso de cartões de crédito. estará disponivel em breve para resgate.">
                <IconButton size="small" sx={{ padding: 0 }}>
                  <InfoOutlinedIcon sx={{ color: "#7600ED", fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography fontSize={22} fontWeight={700}>
              {formatPrice(data?.confirmedBalance)}
            </Typography>

            <Stack>
              <Button
                variant="text"
                size="small"
                sx={{ color: "secondary.contrastText", fontSize: "12px" }}
                onClick={() => {
                  setValue("2");
                }}
              >
                Ver pagamentos
              </Button>
            </Stack>
          </Stack>
        </Grid2>{" "}
        <Grid2
          sx={{
            backgroundColor: "secondary.dark",
            padding: 2,
            borderRadius: 2,
          }}
          size={{ xs: 12, sm: 6, lg: 4 }}
        >
          <Stack
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography fontSize={12} color="secondary.contrastText">
                Pagamentos em aberto
              </Typography>
              <Tooltip title="Este valor corresponde a cobranças que ainda não foram pagas, mas estão dentro do prazo ou agendadas para pagamento em uma data futura.">
                <IconButton size="small" sx={{ padding: 0 }}>
                  <InfoOutlinedIcon sx={{ color: "#7600ED", fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography fontSize={22} fontWeight={700}>
              {formatPrice(data?.pendingBalance || 0)}
            </Typography>
            <Stack>
              <Button
                variant="text"
                size="small"
                sx={{ color: "secondary.contrastText", fontSize: "12px" }}
                onClick={() => {
                  setValue("3");
                }}
              >
                Ver Pagamentos
              </Button>
            </Stack>
          </Stack>
        </Grid2>{" "}
        <Grid2
          sx={{
            backgroundColor: "secondary.dark",
            padding: 2,
            borderRadius: 2,
          }}
          size={{ xs: 12, sm: 6, lg: 4 }}
        >
          <Stack
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={1}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography fontSize={12} color="secondary.contrastText">
                Pagamentos Atrasados
              </Typography>
              <Tooltip title="São cobranças que ainda não foram pagas e já passaram da data de vencimento.">
                <IconButton size="small" sx={{ padding: 0 }}>
                  <InfoOutlinedIcon sx={{ color: "#7600ED", fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography
              fontSize={22}
              fontWeight={700}
              color={data?.overdueBalance ? "error" : ""}
            >
              {formatPrice(data?.overdueBalance || 0)}
            </Typography>
            <Stack>
              <Button
                variant="text"
                size="small"
                color="error"
                sx={{ fontSize: "12px" }}
                onClick={() => {
                  setValue("4");
                }}
              >
                Ver pagamentos
              </Button>
            </Stack>
          </Stack>
        </Grid2>{" "}
        <Grid2 size={12}>
          <Tabs
            sx={{
              borderColor: "divider",
            }}
            onChange={handleChangeValue}
            value={value}
            textColor="inherit"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="EXTRATO" value="1" />
            <Tab label="CONFIRMADOS" value="2" />
            <Tab label="ABERTO" value="3" />
            <Tab label="ATRASADOS" value="4" />
            {/* <Tab label="ANTECIPAÇÂO" value="5" /> */}
            {user?.storeId === 1 && <Tab label="MERCADO PAGO" value="6" />}{" "}
            {/* Em desenvolvimento */}
          </Tabs>
        </Grid2>
        {value === "1" && (
          <>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Typography fontSize={18} fontWeight={700}>
                Extrato
              </Typography>{" "}
              <Typography
                fontSize={{ xs: 12, sm: 14 }}
                color="secondary.contrastText"
              >
                Transações no período selecionado {data?.extract?.length}
              </Typography>
            </Grid2>
            <Grid2 size={6}>
              <Stack>
                <DateRanger
                  start_date={filters.startDate}
                  end_date={filters.endDate}
                  setstart_date={(i: any) => {
                    setFilters((e: any) => ({
                      ...e,
                      startDate: moment(i).toDate(),
                    }));
                  }}
                  setend_date={(i: any) => {
                    setFilters((e: any) => ({
                      ...e,
                      endDate: moment(i).toDate(),
                    }));
                  }}
                />
              </Stack>
            </Grid2>
            <Grid2 size={12}>
              <PaymentItem extract data={data?.extract} />
            </Grid2>
          </>
        )}
        {value === "2" && (
          <>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Typography fontSize={18} fontWeight={700}>
                Pagamentos confirmados
              </Typography>
              <Typography
                fontSize={{ xs: 12, sm: 14 }}
                color="secondary.contrastText"
              >
                Total de pagamentos confirmados{" "}
                {extractPaymentsData?.confirmedCount || 0}
              </Typography>
            </Grid2>

            <Grid2 size={12}>
              <PaymentItem
                isConfirmed
                refetch={() => refetchExtractPayments()}
                extract
                data={extractPaymentsData?.confirmed || []}
              />
            </Grid2>
          </>
        )}
        {value === "3" && (
          <>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Typography fontSize={18} fontWeight={700}>
                Pagamentos em aberto
              </Typography>{" "}
              <Typography
                fontSize={{ xs: 12, sm: 14 }}
                color="secondary.contrastText"
              >
                Total de pagamentos em aberto{" "}
                {extractPaymentsData?.countPaymentsPending}
              </Typography>
            </Grid2>

            <Grid2 size={12}>
              {extractPaymentsData?.pending
                ?.sort(
                  (a: any, b: any) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime(),
                )
                .map((group: any) => (
                  <Accordion key={group.date}>
                    <AccordionSummary
                      expandIcon={<IconArrowDown />}
                      aria-controls={`panel-${group.date}-content`}
                      id={`panel-${group.date}-header`}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        width="100%"
                        alignItems="center"
                      >
                        <Typography component="span" fontWeight={600}>
                          {moment(group.date).format("DD/MM/YYYY")}
                        </Typography>
                        <Typography component="span" fontWeight={600}>
                          {formatPrice(
                            group.payments.reduce(
                              (total: number, payment: any) =>
                                total + (payment.value || 0),
                              0,
                            ),
                          )}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <PaymentItem
                        refetch={() => refetchExtractPayments()}
                        data={group.payments}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
            </Grid2>
          </>
        )}
        {value === "4" && (
          <>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Typography fontSize={18} fontWeight={700}>
                Pagamentos atrasados
              </Typography>{" "}
              <Typography
                fontSize={{ xs: 12, sm: 14 }}
                color="secondary.contrastText"
              >
                Total de Pagamentos atrasados{" "}
                {extractPaymentsData?.overdue?.length}
              </Typography>
            </Grid2>

            <Grid2 size={12}>
              <PaymentItem
                refetch={() => refetchExtractPayments()}
                data={extractPaymentsData?.overdue || []}
              />
            </Grid2>
          </>
        )}
        {value === "5" && (
          <>
            <Grid2
              size={12}
              sx={{ backgroundColor: "secondary.dark", padding: "1rem" }}
            >
              <Typography fontSize={{ xs: 16, sm: 18 }} fontWeight={700}>
                Antecipação automática de cartão
              </Typography>
              <Typography
                mb={3}
                fontSize={{ xs: 12, sm: 14 }}
                color="secondary.contrastText"
              >
                Automatize a solicitação de antecipação de todas das suas
                cobranças de cartão.
              </Typography>

              {[
                "Receba em 1 dia útil após o pagamento",
                `Taxa de ${formatPercentage(taxaAntecipacao)} ao mês`,
                "Ative e desative a qualquer momento",
              ].map((i) => (
                <Stack flexDirection={"row"} gap={1}>
                  <IconCheck color="green" />
                  <Typography>{i}</Typography>
                </Stack>
              ))}

              {dataAntecipation.anticipationDate &&
                dataAntecipation.anticipation && (
                  <Typography
                    mb={3}
                    fontSize={14}
                    color="secondary.contrastText"
                    mt={4}
                  >
                    Antecipação ativa desde{" "}
                    {formatDate(dataAntecipation.anticipationDate)}
                  </Typography>
                )}

              <Stack flexDirection={"row"} gap={1} mt={4}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  color={dataAntecipation?.anticipation ? "error" : "primary"}
                  onClick={() => {
                    mutate();
                  }}
                >
                  {dataAntecipation?.anticipation
                    ? "Cancelar antercipação"
                    : "Ativar antercipação"}
                </Button>
              </Stack>
            </Grid2>

            <Grid2 size={12}>
              <Stack mb={2}>
                <Typography fontWeight={700}>Perguntas frequentes ?</Typography>
              </Stack>
              <Accordion>
                <AccordionSummary
                  expandIcon={<IconArrowDown />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography component="span">
                    O que é antecipação de recebíveis?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  A antecipação de recebíveis permite que você receba valores de
                  vendas feitas no cartão de crédito antes da data prevista. Em
                  vez de esperar o prazo padrão de repasse, você antecipa e
                  recebe em até 1 dia útil, com uma taxa aplicada sobre o valor.
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary
                  expandIcon={<IconArrowDown />}
                  aria-controls="panel2-content"
                  id="panel2-header"
                >
                  <Typography component="span">
                    Como funciona a Antecipação automática?
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  Ao ativar a antecipação automática, todos os pagamentos no
                  cartão de crédito serão antecipados automaticamente, sem
                  precisar de solicitação manual. O valor será depositado em sua
                  conta em até 1 dia útil, com a taxa de 1.99% já descontada.
                </AccordionDetails>
              </Accordion>
            </Grid2>
          </>
        )}
        {value === "6" && <MercadoPagoTab />}
      </Grid2>

      {/* {open && (
        <ModalTransferPix
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          balance={data?.balance}
          refetch={refetch}
        />
      )} */}
    </ContainerLoading>
  );
};

export default Finance;
